import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useChannelBinding, useUpdateChannelBinding } from '@/lib/api/channel-bindings';
import { ApiError } from '@/lib/api/client';
import { BindingForm } from '@/routes/gateway/binding-form';

export function BindingEditPage({ bindingId }: { bindingId: string }) {
  const bindingQuery = useChannelBinding(bindingId);
  const updateBinding = useUpdateChannelBinding(bindingId);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  if (bindingQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (bindingQuery.isError) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load binding: {bindingQuery.error.message}
        </p>
        <Button asChild variant="outline">
          <Link to="/gateway/connections">Back to connections</Link>
        </Button>
      </div>
    );
  }

  const binding = bindingQuery.data;
  const backToConnection = () =>
    void navigate({
      to: '/gateway/connections/$connectionId',
      params: { connectionId: binding.channelConnectionId },
    });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Edit binding</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {binding.strategyKey} → {binding.agentName} v{binding.versionNumber}
          </p>
        </div>
        <Button variant="outline" onClick={backToConnection}>
          Back
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Binding</CardTitle>
          <CardDescription>Disable a binding by unchecking Enabled.</CardDescription>
        </CardHeader>
        <CardContent>
          <BindingForm
            initial={{
              channelConnectionId: binding.channelConnectionId,
              strategyKey: binding.strategyKey,
              agentId: binding.agentId,
              enabled: binding.enabled,
              surface: binding.surface,
              externalScope: binding.externalScope,
            }}
            submitting={updateBinding.isPending}
            submitLabel="Save binding"
            error={error}
            onCancel={backToConnection}
            onSubmit={(values) => {
              setError(null);
              updateBinding.mutate(values, {
                onSuccess: backToConnection,
                onError: (caught) =>
                  setError(
                    caught instanceof ApiError ? caught.message : 'Could not save the binding.',
                  ),
              });
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
